import type { LobbyTrackActivity } from '../Types/Incident';

// ── Visit status normalisation ──

export type LobbyTrackVisitStatusKind = 'signedIn' | 'signedOut' | 'other';

export function normalizeLobbyTrackVisitStatus(
  value?: string | null,
): LobbyTrackVisitStatusKind {
  if (!value) return 'other';
  const normalized = value.trim().toLowerCase();

  if (normalized === 'signedin' || normalized === 'waitingforsignout') {
    return 'signedIn';
  }
  if (normalized === 'signedout') {
    return 'signedOut';
  }
  return 'other';
}

// ── Local datetime formatter ──
// These values are local wall-clock times from Lobby Track.
// Do NOT append 'Z' or convert to UTC.

export function formatLocalDateTimeString(
  value?: string | null,
): string | null {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const datePart = d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${datePart} · ${timePart}`;
  } catch {
    return null;
  }
}

// ── Composite display helper ──

export type LobbyTrackChipTone = 'success' | 'attention' | 'neutral' | 'info';

export interface LobbyTrackActivityDisplayResult {
  hasLobbyTrack: boolean;
  visitStatusKind: LobbyTrackVisitStatusKind;
  statusLabel?: string;
  primaryText?: string;
  secondaryText?: string;
  chipLabel?: string;
  chipTone?: LobbyTrackChipTone;
}

export function getLobbyTrackActivityDisplay(
  activity?: LobbyTrackActivity | null,
): LobbyTrackActivityDisplayResult {
  if (!activity) {
    return { hasLobbyTrack: false, visitStatusKind: 'other' };
  }

  const kind = normalizeLobbyTrackVisitStatus(activity.visitStatus);
  const checkinFormatted = formatLocalDateTimeString(
    activity.checkedInAtLocalDateTime,
  );
  const checkoutFormatted = formatLocalDateTimeString(
    activity.checkedOutAtLocalDateTime,
  );
  const loc = activity.locationName;
  const host = activity.hostName;

  const appendLocation = (base: string): string => {
    if (loc) return `${base} · ${loc}`;
    return base;
  };

  const secondaryParts: string[] = [];

  let statusLabel: string | undefined;
  let chipLabel: string | undefined;
  let chipTone: LobbyTrackChipTone | undefined;
  let primaryText: string | undefined;

  switch (kind) {
    case 'signedIn': {
      statusLabel = 'Signed In';
      chipLabel = 'Signed In';
      chipTone = 'info';
      if (checkinFormatted) {
        primaryText = appendLocation(
          `Lobby Track signed in: ${checkinFormatted}`,
        );
      } else {
        primaryText = appendLocation('Lobby Track signed in');
      }
      if (!activity.checkedOutAtLocalDateTime) {
        secondaryParts.push('No sign-out recorded');
      }
      break;
    }
    case 'signedOut': {
      statusLabel = 'Signed Out';
      chipLabel = 'Signed Out';
      chipTone = 'success';
      if (checkoutFormatted) {
        primaryText = appendLocation(
          `Lobby Track signed out: ${checkoutFormatted}`,
        );
      } else if (checkinFormatted) {
        primaryText = appendLocation(
          `Lobby Track visit: ${checkinFormatted}`,
        );
      } else {
        primaryText = appendLocation('Lobby Track signed out');
      }
      break;
    }
    default: {
      // other / null — no chip, just context text
      if (checkinFormatted) {
        primaryText = appendLocation(
          `Lobby Track visit: ${checkinFormatted}`,
        );
      } else {
        primaryText = appendLocation('Lobby Track visit');
      }
      break;
    }
  }

  if (host) {
    secondaryParts.push(`Host: ${host}`);
  }

  return {
    hasLobbyTrack: true,
    visitStatusKind: kind,
    statusLabel,
    primaryText,
    secondaryText: secondaryParts.length > 0 ? secondaryParts.join(' · ') : undefined,
    chipLabel,
    chipTone,
  };
}
